import { additionalRowsOf, findMainRow, findMainStatedRow, getCampaign, NEGO, rowsOf, newRow } from './promoModel';
import { toUC } from './promoUnits';
import { refreshProductValues } from './promoValues';
import { isProductEditable } from './promoNego';

// Additional presences of a product (#23521, spec RG-05) : extra quantity / container / location / highlight lines on top
// of the main negotiation or statement line, added to the volume compared with the target. Port of the `#mdMultiPromo`
// handlers (visitStep3-promo.js:3287-3458). `kind` is NEGO or STATED.

const mainOf = (rows, ref, productId, kind) => (kind === NEGO ? findMainRow(rows, ref, productId) : findMainStatedRow(rows, ref, productId));

/** The main line must be complete first (`MultiplePromoEmptyItem*`, JS:3305). */
export function canOpenAdditional(snapshot, ref, productId, kind) {
	const main = mainOf(rowsOf(snapshot, ref, kind), ref, productId, kind);
	if (!main || !main.volume || !main.productContainerId || !main.locationPromoId) return { ok: false, error: 'MultiplePromoEmptyItemMsg' };
	return { ok: true };
}

export const additionalOf = (snapshot, ref, productId, kind) => additionalRowsOf(rowsOf(snapshot, ref, kind), ref, productId);

const withValues = (snapshot, ref, productId, kind, rows) => (kind === NEGO ? refreshProductValues(getCampaign(snapshot, ref.campaignId), rows, ref, productId) : rows);

/**
 * Adds an additional presence `{ volume, containerId, locationId, highlightId }`. Volume, container and location are
 * required ; the same location + highlight as an existing line of the product is refused (`PromoMultiAlreadyExists`).
 */
export function addAdditional(snapshot, ref, productId, kind, input) {
	const campaign = getCampaign(snapshot, ref.campaignId);
	if (!campaign || !isProductEditable(snapshot, campaign, ref, productId)) return { ok: false, error: 'PromoReadonly' };
	const opened = canOpenAdditional(snapshot, ref, productId, kind);
	if (!opened.ok) return opened;

	const volume = parseInt(input?.volume, 10) || 0;
	const containerId = parseInt(input?.containerId, 10) || 0;
	const locationId = parseInt(input?.locationId, 10) || 0;
	const highlightId = parseInt(input?.highlightId, 10) || 0;
	if (!volume || !containerId || !locationId) return { ok: false, error: 'PromoAdditionalIncomplete' };

	const rows = rowsOf(snapshot, ref, kind).map((row) => ({ ...row }));
	const duplicate = rows.some((row) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.productId === productId && row.locationPromoId === locationId && row.locationHighlightId === highlightId);
	if (duplicate) return { ok: false, error: 'PromoMultiAlreadyExists' };

	const main = mainOf(rows, ref, productId, kind);
	const item = newRow(ref, productId, {
		offerKey: main.offerKey,
		local: main.local,
		additional: true,
		volume,
		productContainerId: containerId,
		locationPromoId: locationId,
		locationHighlightId: highlightId,
		volumeUC: toUC(snapshot, productId, volume, containerId),
	});
	return { ok: true, ref, rows: withValues(snapshot, ref, productId, kind, [...rows, item]) };
}

/** Removes an additional presence ; the legacy matched it on container + location (`delMultiHandler`, JS:3374). */
export function removeAdditional(snapshot, ref, productId, kind, target) {
	const campaign = getCampaign(snapshot, ref.campaignId);
	if (!campaign || !isProductEditable(snapshot, campaign, ref, productId)) return { ok: false, error: 'PromoReadonly' };
	const rows = rowsOf(snapshot, ref, kind)
		.filter((row) => !(row.additional && row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.productId === productId && row.productContainerId === target.productContainerId && row.locationPromoId === target.locationPromoId))
		.map((row) => ({ ...row }));
	return { ok: true, ref, rows: withValues(snapshot, ref, productId, kind, rows) };
}
